import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Users, Globe, GraduationCap, SearchX } from 'lucide-react';
import { SEO_CONFIG } from '@/config/seo';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { DirectorySearchBar } from '@/components/directory/DirectorySearchBar';
import { DirectoryCard } from '@/components/directory/DirectoryCard';
import { DirectoryMemberSites } from '@/components/directory/DirectoryMemberSites';
import { SupervisorCard } from '@/components/directory/SupervisorCard';
import { useDirectoryAppraisers } from '@/hooks/useDirectoryAppraisers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Container } from '@/components/g5';
import { AuraButton, LabelChip, StatPill } from '@/components/aura';

/** Read a comma-separated param as string array, filtering empties */
function getArrayParam(params: URLSearchParams, key: string): string[] {
  const val = params.get(key);
  if (!val) return [];
  return val.split(',').map(s => s.trim()).filter(Boolean);
}

/** Aura pill tab — violet tint at rest, solid accent when active. */
const tabTriggerCls =
  'gap-2 rounded-full bg-[color:var(--g5-tint)] px-4 py-2 text-sm font-semibold text-[color:var(--g5-accent)] shadow-none transition-colors duration-150 hover:bg-[color:var(--g5-tint)] data-[state=active]:bg-[color:var(--g5-accent)] data-[state=active]:text-[color:var(--g5-on-accent)] data-[state=active]:shadow-none';

export default function AppraiserDirectory() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'profiles';
  const search = searchParams.get('q') || '';
  const stateFilter = searchParams.get('state') || 'all';
  const licenseTypeFilter = searchParams.get('license') || 'all';
  const specialtyFilter = getArrayParam(searchParams, 'specialty');
  const countyFilter = getArrayParam(searchParams, 'county');
  const experienceFilter = searchParams.get('experience') || 'all';

  const updateParams = useCallback((updates: Record<string, string | string[] | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '' || value === 'all' || (Array.isArray(value) && value.length === 0)) {
          next.delete(key);
        } else if (Array.isArray(value)) {
          next.set(key, value.join(','));
        } else {
          next.set(key, value);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setActiveTab = useCallback((tab: string) => updateParams({ tab: tab === 'profiles' ? null : tab }), [updateParams]);
  const setStateFilter = useCallback((v: string) => updateParams({ state: v }), [updateParams]);
  const setLicenseTypeFilter = useCallback((v: string) => updateParams({ license: v }), [updateParams]);
  const setSpecialtyFilter = useCallback((v: string[]) => updateParams({ specialty: v }), [updateParams]);
  const setCountyFilter = useCallback((v: string[]) => updateParams({ county: v }), [updateParams]);
  const setExperienceFilter = useCallback((v: string) => updateParams({ experience: v }), [updateParams]);

  const [visibleCount, setVisibleCount] = useState(24);
  const handleSearchChange = useCallback((value: string) => {
    updateParams({ q: value });
    setVisibleCount(24);
  }, [updateParams]);

  const {
    appraisers,
    totalCount,
    availableSpecialties,
    availableStates,
    availableCounties,
    isLoading,
  } = useDirectoryAppraisers({ search, stateFilter, specialtyFilter, licenseTypeFilter, countyFilter, experienceFilter });

  useEffect(() => { setVisibleCount(24); }, [stateFilter, licenseTypeFilter, specialtyFilter, countyFilter, experienceFilter]);

  const paginatedAppraisers = useMemo(() => appraisers.slice(0, visibleCount), [appraisers, visibleCount]);
  const hasMore = visibleCount < appraisers.length;

  const supervisors = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return appraisers.filter(a => {
      if (!a.accepting_trainees) return false;
      const years = a.appraiser_since ? currentYear - a.appraiser_since : a.years_experience;
      return years != null && years >= 3;
    });
  }, [appraisers]);

  const statItems = useMemo(() => [
    { value: String(totalCount), label: 'Appraisers' },
    { value: String(availableCounties.length), label: 'Counties served' },
    { value: String(availableSpecialties.length), label: 'Specialties' },
    { value: String(supervisors.length), label: 'Supervisors' },
  ], [totalCount, availableCounties.length, availableSpecialties.length, supervisors.length]);

  const canonicalUrl = `${SEO_CONFIG.baseUrl}/directory`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Find a Real Estate Appraiser | ${SEO_CONFIG.siteName} Directory`,
    description: `Browse certified real estate appraisers in the ${SEO_CONFIG.siteName} directory.`,
    numberOfItems: appraisers.length,
    itemListElement: appraisers.slice(0, 20).map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: { '@type': 'LocalBusiness', name: a.name, url: a.slug ? `${SEO_CONFIG.baseUrl}/directory/${a.slug}` : undefined },
    })),
  };

  const Spinner = (
    <div className="flex flex-col items-center justify-center gap-3 py-24">
      <Loader2 className="h-7 w-7 animate-spin" style={{ color: 'var(--g5-accent)' }} />
      <span className="text-sm" style={{ color: 'var(--g5-sub)' }}>Loading directory…</span>
    </div>
  );

  return (
    <>
      <Helmet defer={false}>
        <title>Find a Real Estate Appraiser | {SEO_CONFIG.siteName} Directory</title>
        <meta name="description" content="Search our directory of certified real estate appraisers. Find licensed professionals for residential, commercial, estate, and specialty appraisals." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`Find a Real Estate Appraiser | ${SEO_CONFIG.siteName}`} />
        <meta property="og:description" content={`Browse certified real estate appraisers in the ${SEO_CONFIG.siteName} directory.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SEO_CONFIG.siteName} />
        <meta property="og:image" content={SEO_CONFIG.defaultOgImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Find a Real Estate Appraiser | ${SEO_CONFIG.siteName}`} />
        <meta name="twitter:description" content="Search our directory of certified real estate appraisers." />
        <meta name="twitter:image" content={SEO_CONFIG.defaultOgImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="ashlar-public flex min-h-screen flex-col" style={{ background: 'var(--g5-bg)' }}>
        <LandingHeader />

        <main className="flex-1">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Hero */}
            <section className="relative overflow-hidden" style={{ borderBottom: '1px solid var(--g5-line)' }}>
              <Container className="relative py-14 md:py-20">
                <LabelChip>Appraiser directory</LabelChip>
                <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight md:text-5xl" style={{ color: 'var(--g5-ink)' }}>
                  Find a certified appraiser
                </h1>
                <p className="mt-5 max-w-2xl text-lg leading-relaxed" style={{ color: 'var(--g5-sub)' }}>
                  Browse our network of licensed real estate appraisers. Search by name, location, license type, or specialty to find the right professional for your assignment.
                </p>

                {totalCount > 0 && (
                  <div className="mt-10 flex max-w-3xl flex-wrap gap-2.5">
                    {statItems.map(s => <StatPill key={s.label} value={s.value} label={s.label} />)}
                  </div>
                )}

                <TabsList className="mt-10 h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                  <TabsTrigger value="profiles" className={tabTriggerCls}><Users className="h-4 w-4" />Appraiser profiles</TabsTrigger>
                  <TabsTrigger value="websites" className={tabTriggerCls}><Globe className="h-4 w-4" />Member websites</TabsTrigger>
                  {supervisors.length > 0 && (
                    <TabsTrigger value="supervisors" className={tabTriggerCls}><GraduationCap className="h-4 w-4" />Find a supervisor</TabsTrigger>
                  )}
                </TabsList>
              </Container>
            </section>

            {/* Profiles */}
            <TabsContent value="profiles" className="mt-0">
              <section style={{ borderBottom: '1px solid var(--g5-line)', background: 'var(--g5-soft)' }}>
                <Container className="py-6">
                  <DirectorySearchBar
                    search={search}
                    onSearchChange={handleSearchChange}
                    stateFilter={stateFilter}
                    onStateFilterChange={setStateFilter}
                    licenseTypeFilter={licenseTypeFilter}
                    onLicenseTypeFilterChange={setLicenseTypeFilter}
                    specialtyFilter={specialtyFilter}
                    onSpecialtyFilterChange={setSpecialtyFilter}
                    countyFilter={countyFilter}
                    onCountyFilterChange={setCountyFilter}
                    experienceFilter={experienceFilter}
                    onExperienceFilterChange={setExperienceFilter}
                    availableStates={availableStates}
                    availableSpecialties={availableSpecialties}
                    availableCounties={availableCounties}
                    resultCount={appraisers.length}
                    totalCount={totalCount}
                  />
                </Container>
              </section>

              <Container className="py-12">
                {isLoading ? Spinner : appraisers.length === 0 ? (
                  <div className="flex flex-col items-center py-24 text-center">
                    <SearchX className="mb-4 h-10 w-10" style={{ color: 'var(--g5-line)' }} strokeWidth={1.5} />
                    <h2 className="font-display text-xl font-bold tracking-tight" style={{ color: 'var(--g5-ink)' }}>No appraisers found</h2>
                    <p className="mt-2 max-w-md leading-relaxed" style={{ color: 'var(--g5-sub)' }}>
                      {totalCount === 0
                        ? 'No appraisers have made their profiles public yet. Check back soon.'
                        : 'Try adjusting your search or filters to find what you’re looking for.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid auto-rows-fr grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {paginatedAppraisers.map(appraiser => <DirectoryCard key={appraiser.id} appraiser={appraiser} />)}
                    </div>
                    {hasMore && (
                      <div className="mt-12 flex justify-center">
                        <AuraButton variant="soft" onClick={() => setVisibleCount(c => c + 24)}>
                          Show {Math.min(24, appraisers.length - visibleCount)} more
                        </AuraButton>
                      </div>
                    )}
                  </>
                )}
              </Container>
            </TabsContent>

            {/* Member websites */}
            <TabsContent value="websites" className="mt-0">
              <DirectoryMemberSites />
            </TabsContent>

            {/* Supervisors */}
            {supervisors.length > 0 && (
              <TabsContent value="supervisors" className="mt-0">
                <Container className="py-12">
                  <div className="mb-8 max-w-2xl">
                    <h2 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl" style={{ color: 'var(--g5-ink)' }}>
                      Looking for a supervisory appraiser?
                    </h2>
                    <p className="mt-3 leading-relaxed" style={{ color: 'var(--g5-sub)' }}>
                      New Jersey requires trainee appraisers to work under a certified supervisor with 3+ years of experience.
                      Browse appraisers open to mentoring trainees and reach out directly through our secure contact form.
                    </p>
                  </div>
                  {isLoading ? Spinner : (
                    <div className="grid auto-rows-fr grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {supervisors.map(appraiser => <SupervisorCard key={appraiser.id} appraiser={appraiser} />)}
                    </div>
                  )}
                </Container>
              </TabsContent>
            )}
          </Tabs>
        </main>

        <LandingFooter />
      </div>
    </>
  );
}
